import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Downshift from 'downshift';
import Dismiss from '../../__partials/dismiss';
import DropdownArrow from '../__partials/dropdown-arrow';
import Option from '../__partials/option';
import OptionGroup from '../__partials/option-group';
import Label from '../../forms/__partials/label';
import HelpText from '../../forms/__partials/help-text';
import idGenerator from '../../__utils/idGenerator';
import classNames from 'classnames';
import {
  isItemSelected,
  isItemHighlighted,
  generateIdGenerator,
  getMaxHeightStyles,
  itemToString,
  KeyCodes,
} from '../../__utils/selectHelpers';
import { OptionsPropType } from '../../__utils/selectPropTypes';

import '../style.scss';

export default class Select extends Component {
  constructor(props) {
    super(props);

    this.id = this.props.id || idGenerator();
    this.qaId = this.props.qaId || this.id;

    this.idGenerator = generateIdGenerator(this.id);
    this.qaIdGenerator = generateIdGenerator(this.qaId);

    let selItem;
    if (this.props.defaultValue) {
      if (['string', 'number'].includes(typeof this.props.defaultValue)) {
        if (this.props.options.length > 0) {
          selItem = this.props.options.find(option => option.value === this.props.defaultValue);
        } else {
          Object.keys(this.props.options).forEach(key => {
            const foundItem = this.props.options[key].options.find(option => option.value === this.props.defaultValue);
            if (foundItem) {
              selItem = foundItem;
            }
          });
        }
      }
    }
    this.state = {
      isMenuOpen: false,
      selectedItem: selItem,
    };
  }

  /**
   * Its possible to not provide a value prop to this component.
   * This is a quick way to check if there was a value prop provided
   */
  shouldUseValueProp() {
    return this.props.hasOwnProperty('value');
  }

  onChange = (selectedItem = null) => {
    // state.selectedItem is not used when this is a controlled component
    if (this.shouldUseValueProp()) {
      this.props.onChange && this.props.onChange(selectedItem);
      this.toggleMenu(false);
      return;
    }

    this.setState({ selectedItem }, () => {
      this.props.onChange && this.props.onChange(this.state.selectedItem);
      this.toggleMenu(false);
    });
  };

  /**
   * An odd thing was happening in DownShift, since we are controlling whether
   * the menu is open, where, when selecting the current selected item via keyboard enter
   * it would only un-highlight the item and not close the menu. This is a way to get around
   * that by checking the changes in DownSift's internal state
   */
  onDownshiftStateChange = ({ highlightedIndex, isOpen }) => {
    highlightedIndex === null && isOpen === false && this.toggleMenu(false);
  };

  toggleMenu = isMenuOpen => {
    if (isMenuOpen && typeof this.props.onMenuOpen === 'function') this.props.onMenuOpen();

    isMenuOpen !== this.state.isMenuOpen && this.setState({ isMenuOpen });
  };

  focusInput() {
    // Sometimes we need to wait until next frame to focus the input to allow
    // other certain event handlers to finish running.
    this.input && setTimeout(() => this.input.focus(), 0);
  }

  renderOptions(downshiftProps, options, baseIndex = 0) {
    return options.map((item, itemIndex) => {
      const idDescriptor = `${item.value}-option`;
      const index = baseIndex + itemIndex;

      const { onClick, ...defaultItemProps } = downshiftProps.getItemProps({ item, index });

      const itemProps = {
        ...defaultItemProps,
        onClick: e => onClick(e) && this.focusInput(),
      };

      return (
        <Option
          {...itemProps}
          key={item.value}
          isSelected={isItemSelected(downshiftProps.selectedItem, item)}
          isHighlighted={isItemHighlighted(downshiftProps.highlightedIndex, index)}
          optionData={item}
          qaId={this.qaIdGenerator(idDescriptor)}
        >
          {this.props.customOption}
        </Option>
      );
    });
  }

  renderOptionGroups(downshiftProps, optionGroups) {
    return Object.keys(optionGroups).reduce(
      (result, groupKey) => {
        const group = optionGroups[groupKey];

        const idDescriptor = `${groupKey}-option-group`;

        const groups = (
          <OptionGroup
            key={groupKey}
            title={group.label}
            id={this.idGenerator(idDescriptor)}
            qaId={this.qaIdGenerator(idDescriptor)}
          >
            {this.renderOptions(downshiftProps, group.options, result.itemIndex)}
          </OptionGroup>
        );

        return {
          groups: [...result.groups, groups],
          // Since options are nested in groups, whe dont have true index
          // for any options that aren't in the first group.
          // This allows us to keep a running index of all options.
          itemIndex: result.itemIndex + group.options.length,
        };
      },
      { groups: [], itemIndex: 0 }
    ).groups;
  }

  /**
   * Options groups are only used if an Object is provided for the options prop.
   * Otherwise, options must be provided as an Array
   */
  shouldRenderOptionGroups() {
    return !Array.isArray(this.props.options);
  }

  render() {
    const {
      options,
      value,
      placeholder,
      label,
      required,
      disabled,
      hideLabel,
      helpText,
      hasError,
      name,
      maxHeight,
      hideDismiss,
    } = this.props;
    const { isMenuOpen, selectedItem = null } = this.state;

    const formItemClasses = classNames('uni-form__item', {
      'uni-form__item--is-error': hasError,
      'uni-form__item--is-disabled': disabled,
    });

    const selectClasses = classNames('uni-select', {
      'uni-select--is-open': isMenuOpen,
    });

    let valueOption = value;
    if (this.shouldUseValueProp() && ['number', 'string'].includes(typeof this.props.value)) {
      if (this.props.options.length > 0) {
        valueOption = this.props.options.find(option => option.value === this.props.value);
      } else {
        Object.keys(this.props.options).forEach(key => {
          const foundItem = this.props.options[key].options.find(option => option.value === this.props.value);
          if (foundItem) {
            valueOption = foundItem;
          }
        });
      }
    }

    return (
      <Downshift
        onChange={this.onChange}
        onStateChange={this.onDownshiftStateChange}
        itemToString={itemToString}
        isOpen={this.state.isMenuOpen}
        selectedItem={this.shouldUseValueProp() ? valueOption : selectedItem}
        onOuterClick={() => this.toggleMenu(false)}
        id={this.id}
      >
        {downshiftProps => {
          const { onKeyDown, ...partialInputProps } = downshiftProps.getInputProps({ placeholder });
          const { inputValue } = downshiftProps;

          const onInputKeyDown = e => {
            // We need to override the inputs given onKeyDown function so the menu
            // opens when the space key or arrow keys are pressed on the input.
            if (e.keyCode === KeyCodes.SPACE) this.toggleMenu(!isMenuOpen);
            if (!isMenuOpen && (e.keyCode === KeyCodes.UP || e.keyCode === KeyCodes.DOWN)) this.toggleMenu(!isMenuOpen);

            // We need to close the select on Esc if its open
            if (isMenuOpen && e.keyCode === KeyCodes.ESC) this.toggleMenu(!isMenuOpen);

            onKeyDown(e);
          };

          const inputProps = {
            ...partialInputProps,
            disabled: disabled,
            onKeyDown: onInputKeyDown,
          };

          return (
            <div className={formItemClasses}>
              {label && (
                <Label label={label} required={required} name={this.idGenerator('input')} hideLabel={hideLabel} />
              )}
              <div
                className={selectClasses}
                id={this.id}
                data-qa-id={this.qaId}
                onClick={() => this.toggleMenu(!isMenuOpen)}
              >
                <div className="uni-select__input-container">
                  <input
                    {...inputProps}
                    className="uni-select__input"
                    readOnly
                    id={this.idGenerator('input')}
                    data-qa-id={this.qaIdGenerator('input')}
                    ref={i => (this.input = i)}
                  />

                  <span className="uni-select__input-controls">
                    {(inputValue && !hideDismiss) &&  (
                      <Dismiss
                        size="small"
                        className="uni-select__input-dismiss"
                        onClick={() => {
                          downshiftProps.clearSelection();
                          downshiftProps.openMenu();
                        }}
                      />
                    )}

                    <DropdownArrow />
                  </span>
                </div>

                {!this.shouldUseValueProp() && (
                  <input
                    type="hidden"
                    value={selectedItem ? selectedItem.value : ''}
                    id={this.idGenerator('hidden-input')}
                    data-qa-id={this.qaIdGenerator('hidden-input')}
                    name={name}
                  />
                )}

                <ul
                  className="uni-select__items"
                  style={getMaxHeightStyles(maxHeight, isMenuOpen)}
                  data-qa-id={this.qaIdGenerator('options-list')}
                  id={this.idGenerator('options-list')}
                  role="presentation"
                >
                  {this.shouldRenderOptionGroups()
                    ? this.renderOptionGroups(downshiftProps, options)
                    : this.renderOptions(downshiftProps, options)}
                </ul>
              </div>

              {helpText && <HelpText name={this.idGenerator('input')} text={helpText} hasError={hasError} />}
            </div>
          );
        }}
      </Downshift>
    );
  }
}

Select.propTypes = {
  options: OptionsPropType,
  customOption: PropTypes.any,
  onChange: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  onMenuOpen: PropTypes.func,
  placeholder: PropTypes.string,
  id: PropTypes.string,
  qaId: PropTypes.string,
  label: PropTypes.string,
  hideLabel: PropTypes.bool,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  helpText: PropTypes.string,
  hasError: PropTypes.bool,
  name: PropTypes.string,
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  hideDismiss: PropTypes.bool,
};



// WEBPACK FOOTER //
// ./src/selects/single-select/index.jsx